// pages/DataRefreshQueryModal.js
// Admin → Data Refresh → Query: view and edit the SQL behind one shared snapshot.
//   • Test runs the editor's text read-only (server: SELECT-only check + rolled-back
//     transaction) with the dataset's real parameters, and previews the result.
//   • Save is enabled only after a successful Test of exactly the text being saved.
//   • Reset returns to the query built into the code; History restores any version
//     (into the editor — it still has to pass Test before saving).
// Backend: /api/diSnapshotAdmin (view=query, test-query, save-query).

import { useCallback, useEffect, useState } from 'react';
import Modal from '../components/Modal';

async function api(method, body, qs = '') {
  const r = await fetch(`/api/diSnapshotAdmin${qs}`, {
    method,
    credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const text = await r.text();
  let j = null;
  try { j = text ? JSON.parse(text) : null; } catch { /* non-JSON error */ }
  if (!r.ok) throw new Error((j && j.error) || text || `HTTP ${r.status}`);
  return j;
}

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');
const cell = (v) => (v == null ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v));

export default function DataRefreshQueryModal({ dataset, label, onClose, onSaved, flash }) {
  const [view, setView] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const [text, setText] = useState('');
  const [note, setNote] = useState('');
  const [testing, setTesting] = useState(false);
  const [test, setTest] = useState(null);          // { forText, result } | { forText, error }
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(async () => {
    try {
      const v = await api('GET', null, `?view=query&dataset=${encodeURIComponent(dataset)}`);
      setView(v);
      setText(v.current || '');
      setLoadErr(null);
    } catch (e) { setLoadErr(e.message); }
  }, [dataset]);
  useEffect(() => { load(); }, [load]);

  const dirty = view && text !== view.current;
  const testedOk = test && test.result && test.forText === text;
  const isBuiltIn = view && text.trim() === (view.builtIn || '').trim();

  async function runTest() {
    setTesting(true);
    const forText = text;
    try {
      const result = await api('POST', { action: 'test-query', dataset, sql: forText });
      setTest({ forText, result });
    } catch (e) {
      setTest({ forText, error: e.message });
    } finally { setTesting(false); }
  }

  async function save({ reset = false, refresh = false } = {}) {
    if (reset && !window.confirm('Go back to the query built into the code? The current version stays in History.')) return;
    setSaving(true);
    try {
      const r = await api('POST', {
        action: 'save-query', dataset, sql: reset ? null : text, note: note.trim() || null, refresh,
      });
      setView(r.query);
      setText(r.query.current || '');
      setNote('');
      setTest(null);
      flash && flash(`${label}: query ${reset ? 'reset to built-in' : 'saved'}${refresh ? (r.kicked === false ? ' — but the refresh could not start (see Run history)' : ' — refreshing now') : ' — it applies from the next refresh'}.`, refresh && r.kicked === false);
      onSaved && onSaved();
    } catch (e) {
      flash && flash(`Couldn't save the query: ${e.message}`, true);
    } finally { setSaving(false); }
  }

  const dropped = test && test.result && view
    ? (view.currentColumns || []).filter(c => !test.result.columns.includes(c)) : [];

  const footer = view && view.editable ? (
    <div className="drq-footer">
      <span className="drq-status">
        {!dirty ? 'No changes' : testedOk ? 'Tested — ready to save' : 'Test the query before saving'}
      </span>
      <span className="drq-actions">
        {view.override && (
          <button className="admin-btn admin-btn--secondary" disabled={saving} onClick={() => save({ reset: true })}>
            Reset to built-in
          </button>
        )}
        <button className="admin-btn admin-btn--secondary" disabled={testing || !text.trim()} onClick={runTest}>
          {testing ? 'Testing…' : 'Test'}
        </button>
        <button className="admin-btn admin-btn--secondary" disabled={!dirty || !testedOk || saving} onClick={() => save()}>
          Save
        </button>
        <button className="admin-btn" disabled={!dirty || !testedOk || saving} onClick={() => save({ refresh: true })}>
          {saving ? 'Saving…' : 'Save & refresh'}
        </button>
      </span>
    </div>
  ) : null;

  return (
    <Modal open onClose={onClose} title={`Query — ${label}`} wide footer={footer}>
      {loadErr && <div className="admin-flash error">Couldn't load the query ({loadErr}).</div>}
      {!view && !loadErr && <p className="admin-hint">Loading…</p>}
      {view && !view.editable && (
        <p className="admin-hint">This dataset is computed in code from other snapshots, so it has no query to edit.</p>
      )}
      {view && view.editable && (
        <div className="drq">
          <div className="drq-meta">
            {view.override
              ? <span className="dr-tag dr-tag--edited">Edited by {view.override.by} · {fmtTime(view.override.at)}{view.override.note ? ` — ${view.override.note}` : ''}</span>
              : <span className="dr-tag">Built-in query</span>}
            {view.history.length > 0 && (
              <button className="dr-linkbtn drq-histbtn" onClick={() => setShowHistory(v => !v)}>
                {showHistory ? 'Hide history' : `History (${view.history.length})`}
              </button>
            )}
          </div>

          {showHistory && (
            <div className="drq-history">
              {view.history.map(h => (
                <div key={h.id} className="drq-history-row">
                  <span>{fmtTime(h.at)} · {h.by}{h.note ? ` — ${h.note}` : ''}{h.reset ? ' (reset to built-in)' : ''}</span>
                  <button className="dr-linkbtn" onClick={() => { setText(h.reset ? view.builtIn : h.sql); setTest(null); }}>
                    Load into editor
                  </button>
                </div>
              ))}
            </div>
          )}

          <p className="admin-hint drq-hint">
            Read-only SQL against this database (the <code>pe.*</code> mirror, <code>billing.*</code>, <code>rprs.*</code>, …):
            it must start with SELECT, WITH or DECLARE, and it runs inside a transaction that is always rolled back.
            Keep the column names — the section reads them.
            {view.postProcessed && ' The app post-processes these rows (e.g. converts flags), so keep the same columns.'}
          </p>
          {Object.keys(view.paramDocs || {}).length > 0 && (
            <ul className="drq-params">
              {Object.entries(view.paramDocs).map(([k, d]) => <li key={k}><code>@{k}</code> — {d}</li>)}
            </ul>
          )}

          <textarea
            className="drq-editor"
            spellCheck={false}
            value={text}
            onChange={e => setText(e.target.value)}
          />
          <div className="drq-under">
            <input className="admin-input drq-note" placeholder="Note for the history (optional)" value={note}
              onChange={e => setNote(e.target.value)} maxLength={400} />
            {!isBuiltIn && <button className="dr-linkbtn" onClick={() => { setText(view.builtIn); setTest(null); }}>Load built-in query</button>}
          </div>

          {test && test.forText !== text && <p className="admin-hint">The query changed since the last test — test again.</p>}
          {test && test.error && test.forText === text && <div className="admin-flash error drq-error">{test.error}</div>}
          {test && test.result && test.forText === text && (
            <div className="drq-result">
              <div className="drq-result-head">
                <strong>{test.result.rowCount.toLocaleString()} rows</strong> in {(test.result.durationMs / 1000).toFixed(1)} s
                {' · '}{test.result.columns.length} columns
                {test.result.rowCount === 0 && <span className="dr-stale"> — 0 rows would be rejected as a refresh</span>}
              </div>
              {dropped.length > 0 && (
                <div className="admin-flash error drq-error">
                  Missing columns the current data has: {dropped.join(', ')}. The section may break if it reads them.
                </div>
              )}
              <div className="drq-sample">
                <table className="admin-table dr-table">
                  <thead><tr>{test.result.columns.map(c => <th key={c}>{c}</th>)}</tr></thead>
                  <tbody>
                    {test.result.sample.map((r, i) => (
                      <tr key={i}>{test.result.columns.map(c => <td key={c}>{cell(r[c])}</td>)}</tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
